import { useState, useCallback, useRef, useEffect } from 'react'
import {
  X, ChevronDown, ChevronRight, Clock, Minus, Plus, RotateCcw,
  LayoutDashboard, Briefcase, ClipboardList, Map, FileSpreadsheet, Workflow,
  FileText, GitBranch, Users, BarChart3,
  Target, Activity, CalendarDays, Wrench, Presentation, FileCheck,
  Search, BookOpen, MessageSquareText, FlaskConical, Route, MessageCircleQuestion,
  Trash2, Database, ShieldCheck, Rocket, Ban, Brain, PlusCircle, HardHat,
} from 'lucide-react'
import { roadmapTree as initialTree } from './data'
import type { RoadmapNode, NodeStatus } from './data'

// ── Icon resolver ────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_MAP: Record<string, any> = {
  LayoutDashboard, Briefcase, ClipboardList, Map, FileSpreadsheet, Workflow,
  FileText, GitBranch, Users, BarChart3,
  Target, Activity, CalendarDays, Wrench, Presentation, FileCheck,
  Search, BookOpen, MessageSquareText, FlaskConical, Route, MessageCircleQuestion,
  Trash2, Database, ShieldCheck, Rocket, Ban, Brain, HardHat,
}

function NodeIcon({ name, size = 16, className }: { name: string; size?: number; className?: string }) {
  const Icon = ICON_MAP[name]
  if (!Icon) return null
  return <Icon size={size} className={className} />
}

// ── Status styling ───────────────────────────────────────────────────────────

const statusStyles: Record<NodeStatus, { border: string; bg: string; badge?: string; badgeText?: string }> = {
  live: { border: 'border-[var(--border-color)]', bg: 'bg-[var(--bg-secondary)]' },
  'coming-soon': {
    border: 'border-amber-500/40 border-dashed',
    bg: 'bg-amber-500/5',
    badge: 'bg-amber-500/20',
    badgeText: 'text-amber-400',
  },
  planned: {
    border: 'border-slate-500/30 border-dashed',
    bg: 'bg-slate-500/5',
    badge: 'bg-slate-500/20',
    badgeText: 'text-slate-400',
  },
  deprecated: {
    border: 'border-red-500/40 border-dashed',
    bg: 'bg-red-500/5',
    badge: 'bg-red-500/20',
    badgeText: 'text-red-400',
  },
  'moving-to-frankie-brain': {
    border: 'border-violet-500/40 border-dashed',
    bg: 'bg-violet-500/5',
    badge: 'bg-violet-500/20',
    badgeText: 'text-violet-400',
  },
  'under-construction': {
    border: 'border-orange-500/40 border-dashed',
    bg: 'bg-orange-500/5',
    badge: 'bg-orange-500/20',
    badgeText: 'text-orange-400',
  },
}

const STATUS_LABEL: Record<NodeStatus, string> = {
  live: 'Live',
  'coming-soon': 'Coming Soon',
  planned: 'Planned',
  deprecated: 'Deprecating',
  'moving-to-frankie-brain': 'Moving to Frankie Brain',
  'under-construction': 'Under Construction',
}

// ── Detail panel ─────────────────────────────────────────────────────────────

function DetailPanel({ node, onClose, onSetStatus, onAddChild, onDelete }: {
  node: RoadmapNode
  onClose: () => void
  onSetStatus: (id: string, status: NodeStatus) => void
  onAddChild: (parentId: string, child: RoadmapNode) => void
  onDelete: (id: string) => void
}) {
  const ss = statusStyles[node.status]
  const [showAddForm, setShowAddForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [newStatus, setNewStatus] = useState<NodeStatus>('coming-soon')
  const [newIcon, setNewIcon] = useState('Target')
  const [newIconColor, setNewIconColor] = useState('text-blue-400')

  const handleAdd = () => {
    if (!newName.trim()) return
    const id = newName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    onAddChild(node.id, {
      id: `${node.id}-${id}`,
      label: newName.trim(),
      description: newDesc.trim() || newName.trim(),
      icon: newIcon,
      iconColor: newIconColor,
      status: newStatus,
    })
    setNewName('')
    setNewDesc('')
    setNewStatus('coming-soon')
    setShowAddForm(false)
  }

  return (
    <div
      className="fixed top-0 right-0 h-full w-[400px] max-w-[90vw] z-50 border-l border-[var(--border-color)] bg-[var(--bg-primary)] overflow-y-auto shadow-2xl"
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 p-5 pb-4 bg-[var(--bg-primary)] border-b border-[var(--border-color)]">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-2 rounded-lg ${ss.bg} border ${ss.border}`}>
            <NodeIcon name={node.icon} size={18} className={node.iconColor} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-[var(--text-primary)] leading-tight">{node.label}</h3>
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest mt-1 px-1.5 py-0.5 rounded ${ss.badge || 'bg-emerald-500/20'} ${ss.badgeText || 'text-emerald-400'}`}>
              {node.status === 'deprecated' ? <Ban size={10} /> : node.status === 'moving-to-frankie-brain' ? <Brain size={10} /> : node.status === 'under-construction' ? <HardHat size={10} /> : <Clock size={10} />}
              {STATUS_LABEL[node.status]}
            </span>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors shrink-0">
          <X size={16} />
        </button>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{node.description}</p>

        {/* Status actions */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)] mb-2">Set Status</p>
          <div className="flex flex-wrap gap-1.5">
            {(['live', 'coming-soon', 'under-construction', 'planned', 'deprecated', 'moving-to-frankie-brain'] as NodeStatus[]).map((s) => {
              const active = node.status === s
              const st = statusStyles[s]
              return (
                <button
                  key={s}
                  onClick={() => onSetStatus(node.id, s)}
                  className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border transition-all ${active ? `${st.badge || 'bg-emerald-500/20'} ${st.badgeText || 'text-emerald-400'} ${st.border} ring-1 ring-current` : `border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]`}`}
                >
                  {s === 'deprecated' ? <Ban size={10} /> : s === 'moving-to-frankie-brain' ? <Brain size={10} /> : s === 'under-construction' ? <HardHat size={10} /> : null}
                  {STATUS_LABEL[s]}
                </button>
              )
            })}
          </div>
        </div>

        {/* Delete node */}
        {node.id !== 'dealhub' && (
          <div>
            <button
              onClick={() => { if (confirm(`Delete "${node.label}"${node.children?.length ? ` and its ${node.children.length} children` : ''}?`)) onDelete(node.id) }}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 size={12} />
              Delete Node
            </button>
          </div>
        )}

        {/* Add child */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Children</p>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-[var(--accent-color)] hover:text-[var(--text-primary)] transition-colors"
            >
              <PlusCircle size={12} />
              Add Child
            </button>
          </div>

          {showAddForm && (
            <div className="p-3 rounded-lg border border-[var(--accent-color)]/30 bg-[var(--bg-secondary)] space-y-2.5 mb-3">
              <input
                type="text"
                placeholder="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full text-xs px-2.5 py-1.5 rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)]"
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                autoFocus
              />
              <textarea
                placeholder="Description (optional)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                rows={2}
                className="w-full text-xs px-2.5 py-1.5 rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)] resize-none"
              />
              <div className="flex gap-2">
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as NodeStatus)}
                  className="flex-1 text-xs px-2 py-1.5 rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]"
                >
                  {(['live', 'coming-soon', 'under-construction', 'planned', 'deprecated', 'moving-to-frankie-brain'] as NodeStatus[]).map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </select>
              </div>
              {/* Icon picker */}
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Icon</p>
                <div className="flex flex-wrap gap-1 max-h-[80px] overflow-y-auto">
                  {AVAILABLE_ICONS.map((name) => (
                    <button
                      key={name}
                      onClick={() => setNewIcon(name)}
                      className={`p-1.5 rounded-md border transition-all ${newIcon === name ? 'border-[var(--accent-color)] bg-[var(--accent-color)]/10' : 'border-transparent hover:bg-[var(--bg-tertiary)]'}`}
                      title={name}
                    >
                      <NodeIcon name={name} size={14} className={newIcon === name ? newIconColor : 'text-[var(--text-muted)]'} />
                    </button>
                  ))}
                </div>
              </div>
              {/* Color picker */}
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Color</p>
                <div className="flex flex-wrap gap-1">
                  {ICON_COLORS.map((color) => {
                    const bgColor = color.replace('text-', 'bg-').replace('-400', '-500')
                    return (
                      <button
                        key={color}
                        onClick={() => setNewIconColor(color)}
                        className={`w-5 h-5 rounded-full ${bgColor} transition-all ${newIconColor === color ? 'ring-2 ring-offset-1 ring-offset-[var(--bg-secondary)] ring-[var(--accent-color)] scale-110' : 'opacity-60 hover:opacity-100'}`}
                      />
                    )
                  })}
                </div>
              </div>
              {/* Preview + actions */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] flex items-center justify-center">
                    <NodeIcon name={newIcon} size={16} className={newIconColor} />
                  </div>
                  <span className="text-[10px] text-[var(--text-secondary)]">{newName || 'Preview'}</span>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => setShowAddForm(false)} className="text-[10px] font-semibold px-2 py-1 rounded-md border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handleAdd}
                    disabled={!newName.trim()}
                    className="text-[10px] font-semibold px-2 py-1 rounded-md bg-[var(--accent-color)] text-white hover:opacity-90 transition-colors disabled:opacity-40"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {node.children && node.children.length > 0 && (
            <div className="space-y-2">
              {node.children.map((child) => {
                const cs = statusStyles[child.status]
                return (
                  <div key={child.id} className={`flex items-start gap-2.5 p-2.5 rounded-lg ${cs.bg} border ${cs.border}`}>
                    <NodeIcon name={child.icon} size={14} className={`${child.iconColor} shrink-0 mt-0.5`} />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[var(--text-primary)]">{child.label}</p>
                      <p className="text-[11px] text-[var(--text-muted)] mt-0.5 leading-relaxed">{child.description}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Tree node component ──────────────────────────────────────────────────────

function TreeNode({
  node,
  selectedId,
  onSelect,
  expanded,
  onToggle,
  depth,
}: {
  node: RoadmapNode
  selectedId: string | null
  onSelect: (node: RoadmapNode) => void
  expanded: Record<string, boolean>
  onToggle: (id: string) => void
  depth: number
}) {
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expanded[node.id] !== false // default expanded
  const isSelected = selectedId === node.id
  const ss = statusStyles[node.status]

  return (
    <div className="flex flex-col items-center">
      {/* Node card */}
      <div
        data-node
        onClick={() => onSelect(node)}
        className={`
          relative cursor-pointer select-none group
          flex flex-col items-center gap-1 w-[72px] transition-all duration-200
        `}
      >
        <div className={`w-10 h-10 rounded-lg border flex items-center justify-center transition-all duration-200 ${ss.border} ${ss.bg} ${isSelected ? 'ring-2 ring-[var(--accent-color)] shadow-lg scale-110' : 'group-hover:shadow-md group-hover:scale-105'}`}>
          <NodeIcon name={node.icon} size={18} className={node.iconColor} />
        </div>
        <span className="text-[9px] font-medium text-[var(--text-secondary)] group-hover:text-[var(--accent-color)] transition-colors text-center leading-tight max-w-[72px]">
          {node.label}
        </span>
        {node.status !== 'live' && (
          <span className={`inline-flex items-center gap-0.5 text-[7px] font-semibold uppercase tracking-wider px-1 py-0.5 rounded ${ss.badge} ${ss.badgeText}`}>
            {node.status === 'deprecated' ? <Ban size={6} /> : node.status === 'moving-to-frankie-brain' ? <Brain size={6} /> : node.status === 'under-construction' ? <HardHat size={6} /> : <Clock size={6} />}
            {node.status === 'coming-soon' ? 'Soon' : node.status === 'deprecated' ? 'Deprecating' : node.status === 'moving-to-frankie-brain' ? 'FB' : node.status === 'under-construction' ? 'WIP' : 'Planned'}
          </span>
        )}
        {hasChildren && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(node.id) }}
            className="absolute -top-1 -right-1 p-0.5 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] transition-colors"
          >
            {isExpanded
              ? <ChevronDown size={10} />
              : <ChevronRight size={10} />
            }
          </button>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="flex flex-col items-center">
          {/* Vertical connector from parent */}
          <div className="w-px h-5 bg-[var(--border-color)]" />

          {/* Children row with horizontal bar */}
          <div className="flex items-start gap-3">
            {node.children!.map((child, i) => (
              <div key={child.id} className="flex flex-col items-center">
                {/* T-connector: horizontal bar + vertical drop */}
                {node.children!.length > 1 ? (
                  <div className="flex items-start self-stretch">
                    {/* Left half of horizontal bar */}
                    <div className={`flex-1 h-px ${i === 0 ? '' : 'bg-[var(--border-color)]'}`} />
                    {/* Vertical drop */}
                    <div className="w-px h-5 bg-[var(--border-color)]" />
                    {/* Right half of horizontal bar */}
                    <div className={`flex-1 h-px ${i === node.children!.length - 1 ? '' : 'bg-[var(--border-color)]'}`} />
                  </div>
                ) : (
                  <div className="w-px h-5 bg-[var(--border-color)]" />
                )}
                <TreeNode
                  node={child}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  expanded={expanded}
                  onToggle={onToggle}
                  depth={depth + 1}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'dealhub-roadmap-tree'

function loadTree(): RoadmapNode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) return JSON.parse(saved)
  } catch { /* ignore */ }
  return initialTree
}

const DEFAULT_ZOOM = 0.75
const DEFAULT_PAN = { x: 40, y: 40 }

function cloneTreeWithStatus(node: RoadmapNode, id: string, status: NodeStatus): RoadmapNode {
  if (node.id === id) return { ...node, status }
  if (!node.children) return node
  return { ...node, children: node.children.map((c) => cloneTreeWithStatus(c, id, status)) }
}

function findNode(node: RoadmapNode, id: string): RoadmapNode | null {
  if (node.id === id) return node
  if (node.children) for (const c of node.children) { const r = findNode(c, id); if (r) return r }
  return null
}

function addChildToNode(node: RoadmapNode, parentId: string, child: RoadmapNode): RoadmapNode {
  if (node.id === parentId) return { ...node, children: [...(node.children || []), child] }
  if (!node.children) return node
  return { ...node, children: node.children.map((c) => addChildToNode(c, parentId, child)) }
}

function removeNodeFromTree(node: RoadmapNode, targetId: string): RoadmapNode | null {
  if (node.id === targetId) return null
  if (!node.children) return node
  const filtered = node.children.map((c) => removeNodeFromTree(c, targetId)).filter((c): c is RoadmapNode => c !== null)
  return { ...node, children: filtered }
}

const AVAILABLE_ICONS = Object.keys(ICON_MAP)

const ICON_COLORS = [
  'text-blue-400', 'text-purple-400', 'text-emerald-400', 'text-amber-400',
  'text-cyan-400', 'text-pink-400', 'text-indigo-400', 'text-orange-400',
  'text-violet-400', 'text-teal-400', 'text-red-400', 'text-green-400',
  'text-sky-400', 'text-rose-400', 'text-lime-400', 'text-slate-400',
]

export function RoadmapTool() {
  const [tree, setTree] = useState<RoadmapNode>(loadTree)
  const [selectedNode, setSelectedNode] = useState<RoadmapNode | null>(null)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tree))
  }, [tree])
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [zoom, setZoomRaw] = useState(DEFAULT_ZOOM)
  const [panX, setPanX] = useState(DEFAULT_PAN.x)
  const [panY, setPanY] = useState(DEFAULT_PAN.y)
  const [isPanning, setIsPanning] = useState(false)
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 })

  const setZoom = useCallback((v: number) => setZoomRaw(Math.max(0.2, Math.min(2, v))), [])
  const setPan = useCallback((x: number, y: number) => { setPanX(x); setPanY(y) }, [])

  const handleSelect = useCallback((node: RoadmapNode) => {
    setSelectedNode((prev) => prev?.id === node.id ? null : node)
  }, [])

  const handleSetStatus = useCallback((id: string, status: NodeStatus) => {
    setTree((prev) => {
      const updated = cloneTreeWithStatus(prev, id, status)
      const updatedNode = findNode(updated, id)
      if (updatedNode) setSelectedNode(updatedNode)
      return updated
    })
  }, [])

  const handleAddChild = useCallback((parentId: string, child: RoadmapNode) => {
    setTree((prev) => {
      const updated = addChildToNode(prev, parentId, child)
      const updatedParent = findNode(updated, parentId)
      if (updatedParent) setSelectedNode(updatedParent)
      return updated
    })
  }, [])

  const handleDeleteNode = useCallback((id: string) => {
    setTree((prev) => removeNodeFromTree(prev, id) || prev)
    setSelectedNode(null)
  }, [])

  const handleToggle = useCallback((id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: prev[id] === false ? true : false }))
  }, [])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      setZoom(zoom + (e.deltaY > 0 ? -0.1 : 0.1))
    } else {
      setPan(panX - e.deltaX, panY - e.deltaY)
    }
  }, [zoom, panX, panY, setZoom, setPan])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('[data-node]') || target.closest('button') || target.closest('[data-sidebar]')) return
    if (e.button === 0 || e.button === 1) {
      setIsPanning(true)
      panStart.current = { x: e.clientX, y: e.clientY, panX, panY }
      const handleMouseMove = (ev: MouseEvent) => {
        setPan(panStart.current.panX + ev.clientX - panStart.current.x, panStart.current.panY + ev.clientY - panStart.current.y)
      }
      const handleMouseUp = () => {
        setIsPanning(false)
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }
  }, [panX, panY, setPan])

  // Count nodes
  const countNodes = (node: RoadmapNode): number => {
    let c = 1
    if (node.children) for (const child of node.children) c += countNodes(child)
    return c
  }
  const totalNodes = countNodes(tree)
  const liveNodes = (function countLive(n: RoadmapNode): number {
    let c = n.status === 'live' ? 1 : 0
    if (n.children) for (const ch of n.children) c += countLive(ch)
    return c
  })(tree)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-[var(--text-primary)]">DealHub Roadmap</h2>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Product tree — click any node to see details. {liveNodes} of {totalNodes} features live.
          </p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)]" />
            <span className="text-xs text-[var(--text-muted)]">Live</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-dashed border-amber-500/40 bg-amber-500/5" />
            <span className="text-xs text-[var(--text-muted)]">Coming Soon</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-dashed border-slate-500/30 bg-slate-500/5" />
            <span className="text-xs text-[var(--text-muted)]">Planned</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-dashed border-orange-500/40 bg-orange-500/5" />
            <span className="text-xs text-[var(--text-muted)]">Under Construction</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-dashed border-red-500/40 bg-red-500/5" />
            <span className="text-xs text-[var(--text-muted)]">Deprecating</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-dashed border-violet-500/40 bg-violet-500/5" />
            <span className="text-xs text-[var(--text-muted)]">Frankie Brain</span>
          </div>
          <span className="text-xs text-[var(--text-muted)]">Scroll to pan / Ctrl+scroll to zoom / Drag to pan</span>
        </div>
      </div>

      {/* Zoomable / pannable canvas */}
      <div
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        className="relative w-full overflow-hidden rounded-xl border border-[var(--border-color)]"
        style={{
          height: 600,
          cursor: isPanning ? 'grabbing' : 'grab',
          backgroundImage: 'radial-gradient(circle, var(--border-color) 1px, transparent 1px)',
          backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
          backgroundPosition: `${panX}px ${panY}px`,
        }}
      >
        {/* Transformed content layer */}
        <div
          className="absolute inset-0"
          style={{
            transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
            transformOrigin: '0 0',
          }}
        >
          <div className="flex justify-center pt-8 pl-8" style={{ width: 'max-content' }}>
            <TreeNode
              node={tree}
              selectedId={selectedNode?.id ?? null}
              onSelect={handleSelect}
              expanded={expanded}
              onToggle={handleToggle}
              depth={0}
            />
          </div>
        </div>

        {/* Zoom controls (bottom-left, outside transform) */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-1 z-10">
          <button onClick={() => setZoom(zoom - 0.1)} className="p-1.5 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <Minus size={14} />
          </button>
          <span className="text-[11px] font-mono text-[var(--text-muted)] w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(zoom + 0.1)} className="p-1.5 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <Plus size={14} />
          </button>
          <div className="w-px h-4 bg-[var(--border-color)] mx-0.5" />
          <button onClick={() => { setZoom(DEFAULT_ZOOM); setPan(DEFAULT_PAN.x, DEFAULT_PAN.y) }} className="p-1.5 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <RotateCcw size={14} />
          </button>
        </div>
      </div>

      {/* Detail panel */}
      {selectedNode && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setSelectedNode(null)} />
          <DetailPanel node={selectedNode} onClose={() => setSelectedNode(null)} onSetStatus={handleSetStatus} onAddChild={handleAddChild} onDelete={handleDeleteNode} />
        </>
      )}
    </div>
  )
}
